import React, { useEffect } from "react";
import { Flex, Text } from "@chakra-ui/react";
import AppButton from "@/features/line-sheet-sets/app-button";
import CustomApprovalModal from "@/shared/components/custom-modal";
import useI18nHelper from "@/features/ui/hooks/use-i18n-helper";
import PathPickInlineNotice from "@/features/pathpick/components/ui/PathPickInlineNotice";
import PathPickStartGuideMenuPath from "@/features/pathpick/features/start-guide/components/setup/PathPickStartGuideMenuPath";
import PathPickStartGuideExternalLink from "@/features/pathpick/features/start-guide/components/PathPickStartGuideExternalLink";
import useExternalLinkGate from "@/features/pathpick/features/start-guide/hooks/useExternalLinkGate";
import { SMART_STORE_SELL_AUTH_URL } from "@/features/pathpick/features/start-guide/constants";

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onComplete: () => void;
}

/**
 * 해외 판매 권한 신청 안내.
 *
 * 전달서 §8.2 가 명시적으로 금지해서 `네이버 공식 안내 보기` 링크는 두지 않는다 —
 * 다른 세 모달과 다른 점이다. 대신 외부 설정이 자동 확인되지 않는다는 안내를 둔다.
 */
function PathPickStartGuideOverseasPermissionModal({
  isOpen,
  onClose,
  onComplete,
}: Props) {
  const { t } = useI18nHelper();
  const { hasOpened, markOpened, reset } = useExternalLinkGate();

  // 모달을 닫았다 다시 열면 링크를 열었다는 표시도 초기화한다.
  useEffect(() => {
    if (!isOpen) {
      reset();
    }
  }, [isOpen, reset]);

  return (
    <CustomApprovalModal
      isOpen={isOpen}
      onClose={onClose}
      minWidth={"560px"}
      isCentered={true}
    >
      <CustomApprovalModal.Header
        onClose={onClose}
        title={t("path_pick_guide:overseas_permission.title")}
      />
      <CustomApprovalModal.Body>
        <Flex flexDirection={"column"} gap={"12px"}>
          <PathPickStartGuideMenuPath
            path={t("path_pick_guide:overseas_permission.menu_path")}
          />
          <Text textStyle={"body-sm-medium"} color={"#6F6F6C"}>
            {t("path_pick_guide:overseas_permission.description")}
          </Text>
          <PathPickInlineNotice tone={"yellow"} leadingIcon={"info-circle"}>
            {t("path_pick_guide:overseas_permission.manual_notice")}
          </PathPickInlineNotice>
          <Flex
            gap={"8px"}
            flexWrap={"wrap"}
            justifyContent={"flex-end"}
            paddingTop={"4px"}
          >
            <PathPickStartGuideExternalLink
              href={SMART_STORE_SELL_AUTH_URL}
              label={t("path_pick_guide:overseas_permission.open_cta")}
              newWindowLabel={t("path_pick_guide:external_link_suffix")}
              onOpen={markOpened}
              isPrimary={!hasOpened}
            />
            <AppButton
              variant={"primary"}
              isDisabled={!hasOpened}
              onClick={onComplete}
            >
              {t("path_pick_guide:overseas_permission.done_cta")}
            </AppButton>
            <AppButton variant={"normal"} onClick={onClose}>
              {t("path_pick_guide:close")}
            </AppButton>
          </Flex>
        </Flex>
      </CustomApprovalModal.Body>
    </CustomApprovalModal>
  );
}

export default PathPickStartGuideOverseasPermissionModal;
